// Hunter Classification System - Solo Leveling Style
// Analyzes stat distribution to assign a thematic hunter class

export interface HunterClass {
  name: string;
  title: string;
  description: string;
  color: string;
  gradient: string;
  icon: string;
  tier: 'legendary' | 'advanced' | 'intermediate' | 'starting'; // Internal tier for classification logic
}

interface Stats {
  strength: number;
  intelligence: number;
  agility: number;
  vitality: number;
  sense: number;
}

// Calculate stat total and dominant stats
function analyzeStats(stats: Stats): {
  total: number;
  highest: string;
  secondHighest: string;
  isBalanced: boolean;
  dominantValue: number;
} {
  const statArray = [
    { name: 'strength', value: stats.strength },
    { name: 'intelligence', value: stats.intelligence },
    { name: 'agility', value: stats.agility },
    { name: 'vitality', value: stats.vitality },
    { name: 'sense', value: stats.sense },
  ].sort((a, b) => b.value - a.value);

  const total = stats.strength + stats.intelligence + stats.agility + stats.vitality + stats.sense;
  const highest = statArray[0];
  const secondHighest = statArray[1];
  
  // Check if stats are balanced (highest stat is not more than 30% above average)
  const average = total / 5;
  const isBalanced = highest.value < average * 1.3;

  return {
    total,
    highest: highest.name,
    secondHighest: secondHighest.name,
    isBalanced,
    dominantValue: highest.value,
  };
}

/**
 * Determines the hunter class based on stat distribution
 * Inspired by Solo Leveling's hunter types and classes
 */
export function getHunterClass(stats: Stats): HunterClass {
  const analysis = analyzeStats(stats);
  const { total, highest, secondHighest, isBalanced, dominantValue } = analysis;

  // SHADOW MONARCH - Ultimate balanced power (Level 100+, all stats 400+)
  if (total >= 2000 && dominantValue >= 400) {
    return {
      name: 'SHADOW MONARCH',
      title: 'Sovereign of Death',
      description: 'One who has transcended all limits. The strongest existence that commands the power of shadows and death itself.',
      color: '#9333EA',
      gradient: 'from-purple-600 via-black to-purple-600',
      icon: '👑',
      tier: 'legendary',
    };
  }

  // NATIONAL LEVEL HUNTER - Exceptionally high total stats
  if (total >= 1500) {
    if (isBalanced) {
      return {
        name: 'NATIONAL LEVEL HUNTER',
        title: 'Humanity\'s Pinnacle',
        description: 'An elite hunter whose power rivals natural disasters. A strategic asset to entire nations.',
        color: '#EF4444',
        gradient: 'from-red-600 to-orange-500',
        icon: '🔥',
        tier: 'legendary',
      };
    }
  }

  // HIGH LEVEL CLASSES - Based on dominant stats (total >= 1000)
  if (total >= 1000) {
    // SHADOW ASSASSIN - High Agility + Sense
    if ((highest === 'agility' || secondHighest === 'agility') && 
        (highest === 'sense' || secondHighest === 'sense')) {
      return {
        name: 'SHADOW ASSASSIN',
        title: 'Silent Death',
        description: 'A master of stealth and precision. Your enhanced agility and sense make you a phantom on the battlefield.',
        color: '#8B5CF6',
        gradient: 'from-purple-500 to-indigo-600',
        icon: '🗡️',
        tier: 'legendary',
      };
    }

    // ARCHMAGE - High Intelligence
    if (highest === 'intelligence') {
      return {
        name: 'ARCHMAGE',
        title: 'Master of Mana',
        description: 'Your intellect has unlocked the deepest mysteries of magic. Reality bends to your will.',
        color: '#3B82F6',
        gradient: 'from-blue-500 to-cyan-500',
        icon: '🔮',
        tier: 'legendary',
      };
    }

    // TITAN GUARDIAN - High Vitality
    if (highest === 'vitality') {
      return {
        name: 'TITAN GUARDIAN',
        title: 'Unbreakable Wall',
        description: 'An immovable fortress. Your vitality makes you nearly indestructible, protecting all who stand behind you.',
        color: '#10B981',
        gradient: 'from-green-500 to-emerald-600',
        icon: '🛡️',
        tier: 'legendary',
      };
    }

    // BERSERKER - High Strength
    if (highest === 'strength') {
      return {
        name: 'BERSERKER',
        title: 'Overwhelming Force',
        description: 'Pure destructive power flows through you. Your strength crushes all obstacles in your path.',
        color: '#DC2626',
        gradient: 'from-red-600 to-rose-700',
        icon: '⚔️',
        tier: 'legendary',
      };
    }

    // ORACLE - High Sense
    if (highest === 'sense') {
      return {
        name: 'ORACLE',
        title: 'All-Seeing Eye',
        description: 'Your perception transcends normal limits. Nothing escapes your awareness.',
        color: '#F59E0B',
        gradient: 'from-yellow-500 to-orange-500',
        icon: '👁️',
        tier: 'legendary',
      };
    }
  }

  // MID LEVEL CLASSES (total >= 500)
  if (total >= 500) {
    // ASSASSIN - Agility + Sense
    if ((highest === 'agility' || secondHighest === 'agility') && 
        (highest === 'sense' || secondHighest === 'sense')) {
      return {
        name: 'ASSASSIN',
        title: 'Swift Blade',
        description: 'Speed and perception combined. You strike before your enemies even realize you\'re there.',
        color: '#8B5CF6',
        gradient: 'from-purple-400 to-indigo-500',
        icon: '🗡️',
        tier: 'advanced',
      };
    }

    // MAGE - Intelligence focused
    if (highest === 'intelligence') {
      return {
        name: 'MAGE',
        title: 'Spell Weaver',
        description: 'Your magical prowess continues to grow. Each spell cast brings you closer to mastery.',
        color: '#3B82F6',
        gradient: 'from-blue-400 to-cyan-400',
        icon: '✨',
        tier: 'advanced',
      };
    }

    // TANK - Vitality focused
    if (highest === 'vitality') {
      return {
        name: 'TANK',
        title: 'Iron Defender',
        description: 'Your endurance is remarkable. You can withstand punishment that would fell lesser hunters.',
        color: '#10B981',
        gradient: 'from-green-400 to-emerald-500',
        icon: '🛡️',
        tier: 'advanced',
      };
    }

    // FIGHTER - Strength focused
    if (highest === 'strength') {
      return {
        name: 'FIGHTER',
        title: 'Heavy Hitter',
        description: 'Your physical power is formidable. Each strike carries devastating force.',
        color: '#DC2626',
        gradient: 'from-red-500 to-rose-600',
        icon: '💪',
        tier: 'advanced',
      };
    }

    // RANGER - Sense focused
    if (highest === 'sense') {
      return {
        name: 'RANGER',
        title: 'Eagle Eye',
        description: 'Your heightened senses give you an edge. You detect threats long before they arrive.',
        color: '#F59E0B',
        gradient: 'from-yellow-400 to-orange-400',
        icon: '🏹',
        tier: 'advanced',
      };
    }

    // BALANCED - All-Rounder
    if (isBalanced) {
      return {
        name: 'ALL-ROUNDER',
        title: 'Versatile Hunter',
        description: 'Your balanced approach makes you adaptable to any situation. A jack of all trades, master of growth.',
        color: '#06B6D4',
        gradient: 'from-cyan-400 to-blue-400',
        icon: '⚡',
        tier: 'advanced',
      };
    }
  }

  // LOW LEVEL CLASSES (total >= 250)
  if (total >= 250) {
    if (highest === 'agility' || highest === 'sense') {
      return {
        name: 'SCOUT',
        title: 'Swift Observer',
        description: 'Your agility and awareness are developing. Keep training to unlock your true potential.',
        color: '#8B5CF6',
        gradient: 'from-purple-300 to-indigo-400',
        icon: '🏃',
        tier: 'intermediate',
      };
    }

    if (highest === 'intelligence') {
      return {
        name: 'APPRENTICE MAGE',
        title: 'Novice Caster',
        description: 'You\'re beginning to understand the flow of mana. Continue your studies to grow stronger.',
        color: '#3B82F6',
        gradient: 'from-blue-300 to-cyan-300',
        icon: '📚',
        tier: 'intermediate',
      };
    }

    if (highest === 'vitality') {
      return {
        name: 'DEFENDER',
        title: 'Sturdy Protector',
        description: 'Your resilience is growing. With more training, you\'ll become an unbreakable shield.',
        color: '#10B981',
        gradient: 'from-green-300 to-emerald-400',
        icon: '🛡️',
        tier: 'intermediate',
      };
    }

    if (highest === 'strength') {
      return {
        name: 'WARRIOR',
        title: 'Developing Force',
        description: 'Your strength is notable. Keep pushing your limits to reach new heights of power.',
        color: '#DC2626',
        gradient: 'from-red-400 to-rose-500',
        icon: '⚔️',
        tier: 'intermediate',
      };
    }

    return {
      name: 'TRAINEE',
      title: 'Aspiring Hunter',
      description: 'You\'re on the path to greatness. Every quest completed brings you closer to awakening your true power.',
      color: '#06B6D4',
      gradient: 'from-cyan-300 to-blue-300',
      icon: '🎯',
      tier: 'intermediate',
    };
  }

  // BEGINNER LEVEL (total < 250)
  if (total >= 100) {
    return {
      name: 'AWAKENED',
      title: 'Newly Emerged',
      description: 'You\'ve just awakened to your powers. The journey ahead is long, but your potential is limitless.',
      color: '#64748B',
      gradient: 'from-slate-400 to-gray-500',
      icon: '✨',
      tier: 'starting',
    };
  }

  // VERY EARLY GAME
  return {
    name: 'E-RANK HUNTER',
    title: 'World\'s Weakest',
    description: 'Everyone starts somewhere. This is your origin story. Will you become the strongest?',
    color: '#9CA3AF',
    gradient: 'from-gray-400 to-slate-500',
    icon: '🌟',
    tier: 'starting',
  };
}

/**
 * Get a motivational message based on hunter class tier
 */
export function getClassMotivation(tier: 'legendary' | 'advanced' | 'intermediate' | 'starting'): string {
  const messages = {
    legendary: 'You stand among the strongest. Continue to push the boundaries of what\'s possible.',
    advanced: 'Your power is formidable. Keep growing to reach the pinnacle of strength.',
    intermediate: 'You\'re making solid progress. Don\'t stop now—greatness awaits!',
    starting: 'Every legend starts with a single step. Your journey begins now!',
  };
  
  return messages[tier];
}

/**
 * Extract rank letter from user's title (e.g., "S-Rank Hunter" -> "S", "Shadow Monarch" -> "SS")
 */
export function extractRankFromTitle(title: string): string {
  if (title === 'Shadow Monarch') return 'SS';
  const match = title.match(/^([EDCBAS])-Rank/);
  return match ? match[1] : 'E';
}

